import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import InfoIcon from '@mui/icons-material/Info';
import AttachmentsUpload from '../attachments-upload';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useRef, useCallback } from 'react';

import { softObjectBySubObjectMaps } from '../../constants/objects';
import { setSupportedObjects, setSelectedObjects, setAttachmentsImportStatus } from '../../store/actions/Migration';

const FieldObjects = ({ hardObjects, softObjects, type }) => {
    const dispatch = useDispatch();
    const { migration, supportedObjects } = useSelector(st => st['Migration']);
    const pendingApiCall = useRef(null);
    const latestSupportedObjects = useRef(supportedObjects);

    // Keep ref in sync with state
    useEffect(() => { latestSupportedObjects.current = supportedObjects; }, [supportedObjects]);

    useEffect(() => setEnvironment(), []);
    // useEffect(() => { setEnvironment(); }, [migration]);
    const setEnvironment = () => { if (migration['supportedObjects']) dispatch(setSupportedObjects(migration['supportedObjects'])) };

    const isSoftOjectDisabled = (key) => {
        const subObject = softObjectBySubObjectMaps[migration['sourceProvider']]?.get(key);
        if (subObject) {
            key = subObject.key;
        }
        return !supportedObjects.includes(key);
    }

    const selectObject = useCallback((key) => {
        // Use ref to get the latest state to avoid race conditions
        const currentObjects = [...latestSupportedObjects.current];
        let newSupportObj;
        if (!currentObjects.includes(key)) {
            newSupportObj = [...currentObjects, key];
        } else {
            newSupportObj = currentObjects.filter(x => x !== key);
        }

        // Update local state immediately
        dispatch(setSupportedObjects(newSupportObj));
        latestSupportedObjects.current = newSupportObj;

        // Cancel any pending API call and debounce
        if (pendingApiCall.current) clearTimeout(pendingApiCall.current);
        pendingApiCall.current = setTimeout(() => {
            dispatch(setSelectedObjects({ supportedObjects: JSON.stringify(latestSupportedObjects.current), key: migration['key'], migration, skipStateUpdate: true }));
        }, 300);
    }, [dispatch, migration]);

    return (
        <div className='connect-inner object-tab'>
            <div className='title-area'>
                <p>Select the <b>standard objects</b> you want to migrate</p>
            </div>
            {migration['type'] == 'Full migration' &&
                <div className='checkbox-box'>
                    <h4>HARD DATA</h4>
                    <div className='checkbox-area'>
                        {hardObjects.map(({ label, key }) => <div className='label-box'> <Checkbox checked={supportedObjects.includes(key)} onClick={() => selectObject(key)} /> <label> {label}</label> </div>)}
                    </div>
                </div>
            }
            <div className='checkbox-box style-two'>
                <h4>SOFT DATA</h4>
                <div className='checkbox-area'>
                    {softObjects.map(({ label, key, subObjects }) =>
                        <div className='label-box'
                            style={
                                isSoftOjectDisabled(key) && type == 'destination'
                                    ? { opacity: 0.5, cursor: 'not-allowed' }
                                    : {}
                            }
                        >
                            <Checkbox
                                className={isSoftOjectDisabled(key) && type == 'destination' && 'Mui-disabled'}
                                checked={supportedObjects.includes(key)}
                                onClick={() => selectObject(key)}
                            />
                            <label> {label}</label>
                            {subObjects && type == 'source' && <Tooltip title={<div style={{ 'maxWidth': '300px' }}>{`Includes ${subObjects.map(({ label }) => label).join(', ')}`}</div>}> <InfoIcon sx={{ fontSize: 16, color: '#0065ff' }} /> </Tooltip>}
                            {softObjectBySubObjectMaps[migration['sourceProvider']]?.get(key) && type == 'destination' && <Tooltip title={<div style={{ 'maxWidth': '300px' }}>{`Part of ${softObjectBySubObjectMaps[migration['sourceProvider']].get(key).label}`}</div>}> <InfoIcon sx={{ fontSize: 16, color: '#0065ff' }} /> </Tooltip>}
                        </div>
                    )}
                </div>
            </div>
            {migration['customSalesforceObj'] && type == 'source' && migration['customSalesforceObj'].length > 0 &&
                <>
                    <div className='title-area style-two'>
                        <p>Select the <b>custom objects</b> you want to migrate</p>
                    </div>
                    <div className='checkbox-box'>
                        <div className='checkbox-area'>
                            {migration['customSalesforceObj'].map(({ label }) =>
                                <div className='label-box'>
                                    <Checkbox checked={supportedObjects.includes(`${label}_CustomObject`)} onClick={() => selectObject(`${label}_CustomObject`)} />
                                    <label> {label}</label>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            }
            {/* Source custom objects */}
            {migration['hasCustomObjects'] && type == 'source' &&
                <>
                    <div className='title-area style-two'>
                        <p>Select the <b>source custom objects</b> you want to migrate</p>
                    </div>
                    <div className='checkbox-box'>
                        <div className='checkbox-area'>
                            {/* Salesforce custom objects */}
                            {migration['sourceProvider'] === 'salesforce' && migration['customSalesforceObj'] && migration['customSalesforceObj'].length > 0 &&
                                migration['customSalesforceObj'].map(({ key, label }) =>
                                    <div className='label-box' key={`source_${key}`}>
                                        <Checkbox checked={supportedObjects.includes(`${label}_CustomObject`)} onClick={() => selectObject(`${label}_CustomObject`)} />
                                        <label> {label}</label>
                                    </div>
                                )
                            }
                            {/* HubSpot source custom objects */}
                            {migration['sourceProvider'] === 'hubspot' && migration['sourceCustomHubspotObj'] && migration['sourceCustomHubspotObj'].length > 0 &&
                                migration['sourceCustomHubspotObj'].map(({ labels }) =>
                                    <div className='label-box' key={`source_${labels['singular']}`}>
                                        <Checkbox checked={supportedObjects.includes(`${labels['plural']}_CustomObject`)} onClick={() => selectObject(`${labels['plural']}_CustomObject`)} />
                                        <label> {labels['plural']}</label>
                                    </div>
                                )
                            }
                            {/* No source custom objects found */}
                            {((migration['sourceProvider'] === 'salesforce' && (!migration['customSalesforceObj'] || migration['customSalesforceObj'].length === 0)) ||
                                (migration['sourceProvider'] === 'hubspot' && (!migration['sourceCustomHubspotObj'] || migration['sourceCustomHubspotObj'].length === 0))) &&
                                <div className='no-objects-message'>
                                    <p>No source custom objects found.</p>
                                </div>
                            }
                        </div>
                    </div>
                </>
            }
            {/* Destination custom objects */}
            {migration['hasCustomObjects'] && type == 'destination' &&
                <>
                    <div className='title-area style-two'>
                        <p>Select the <b>destination custom objects</b> you want to migrate to</p>
                    </div>
                    <div className='checkbox-box'>
                        <div className='checkbox-area'>
                            {/* HubSpot destination custom objects for HubSpot to HubSpot migrations */}
                            {migration['sourceProvider'] === 'hubspot' && migration['destinationCustomHubspotObj'] && migration['destinationCustomHubspotObj'].length > 0 &&
                                migration['destinationCustomHubspotObj'].map(({ labels }) =>
                                    <div className='label-box' key={`dest_${labels['singular']}`}>
                                        <Checkbox checked={supportedObjects.includes(`${labels['plural']}_CustomObject`)} onClick={() => selectObject(`${labels['plural']}_CustomObject`)} />
                                        <label> {labels['plural']}</label>
                                    </div>
                                )
                            }
                            {/* HubSpot destination custom objects for other providers */}
                            {migration['sourceProvider'] !== 'hubspot' && migration['customHubspotObj'] && migration['customHubspotObj'].length > 0 &&
                                migration['customHubspotObj'].map(({ labels }) =>
                                    <div className='label-box' key={`dest_${labels['singular']}`}>
                                        <Checkbox checked={supportedObjects.includes(`${labels['plural']}_CustomObject`)} onClick={() => selectObject(`${labels['plural']}_CustomObject`)} />
                                        <label> {labels['plural']}</label>
                                    </div>
                                )
                            }
                            {/* No destination custom objects found */}
                            {((migration['sourceProvider'] === 'hubspot' && (!migration['destinationCustomHubspotObj'] || migration['destinationCustomHubspotObj'].length === 0)) ||
                                (migration['sourceProvider'] !== 'hubspot' && (!migration['customHubspotObj'] || migration['customHubspotObj'].length === 0))) &&
                                <div className='no-objects-message'>
                                    <p>No destination custom objects found.</p>
                                </div>
                            }
                        </div>
                    </div>
                </>
            }
            {/* Salesforce Data Export Upload - Shows when Attachments is selected (source only) */}
            {type === 'source' && migration['sourceProvider'] === 'salesforce' && supportedObjects.includes('Attachments_SoftData') && (
                <AttachmentsUpload
                    migrationKey={migration['key']}
                    onStatusChange={(status) => dispatch(setAttachmentsImportStatus(status))}
                    onUploadComplete={(stats) => {
                        console.log('Upload complete:', stats);
                        dispatch(setAttachmentsImportStatus('complete'));
                    }}
                />
            )}
        </div>
    )
}

export default FieldObjects;